import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Button, Table, Form } from 'react-bootstrap';
import { FaShoppingCart, FaTrash } from 'react-icons/fa';
import SEO from '../SEO'; // Import the SEO component

function Cart() {
    const [cartItems, setCartItems] = useState([]);
    const [email, setEmail] = useState(localStorage.getItem('customerEmail') || '');
    const [emailSubmitted, setEmailSubmitted] = useState(!!localStorage.getItem('customerEmail'));
    const [customerName, setCustomerName] = useState('');
    const [contactNumber, setContactNumber] = useState('');

    // Memoize fetchCartItems to prevent redefinition on every render
    const fetchCartItems = useCallback(async () => {
        if (!email) return;
        try {
            const response = await fetch(`https://galleceylonspices.com/api/cart?email=${encodeURIComponent(email)}`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            setCartItems(data);
        } catch (error) {
            console.error('Error fetching cart items:', error);
            setCartItems([]);
            alert('Failed to load cart items. Please try again later or contact support.');
        }
    }, [email]);

    useEffect(() => {
        if (emailSubmitted && email) {
            fetchCartItems();
        }
    }, [emailSubmitted, email, fetchCartItems]);

    const handleEmailSubmit = () => {
        if (!email) {
            alert('Please enter your email to view your cart.');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            alert('Please enter a valid email address.');
            return;
        }

        localStorage.setItem('customerEmail', email);
        setEmailSubmitted(true);
    };

    const handleRemoveItem = async (id) => {
        try {
            const response = await fetch(`https://galleceylonspices.com/api/cart/${id}`, {
                method: 'DELETE',
            });
            if (response.ok) {
                fetchCartItems();
            } else {
                alert('Failed to remove item from cart. Please try again.');
            }
        } catch (error) {
            console.error('Error removing item from cart:', error);
            alert('An error occurred while removing the item. Please contact support.');
        }
    };

    const handleSendQuote = () => {
        if (!customerName || !contactNumber) {
            alert('Please enter your name and contact number.');
            return;
        }

        // Validate contact number (basic phone number format)
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;
        if (!phoneRegex.test(contactNumber)) {
            alert('Please enter a valid contact number (e.g., +94779291829).');
            return;
        }

        const message = `Customer Email: ${email}\nCustomer Name: ${customerName}\nContact Number: ${contactNumber}\n\nCart Quote Request:\n\n${cartItems
            .map(
                (item) =>
                    `Category: ${item.category}\nType: ${item.type}\nQuantity: ${item.quantity}\nPack Weight: ${item.packWeight} ${item.unit}`
            )
            .join('\n\n')}`;
        const whatsappUrl = `https://wa.me/+94779291829?text=${encodeURIComponent(message)}`;
        const opened = window.open(whatsappUrl, '_blank');
        if (!opened) {
            alert('Unable to open WhatsApp. Please contact us at +94 77 929 1829 or galleceylonspices@gmail.com.');
        }
    };

    // SEO Structured Data
    const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Your Cart - Galle Ceylon Spices International",
        "description": "View and manage your shopping cart with premium Ceylon cinnamon and spices from Galle Ceylon Spices International. Request a quote via WhatsApp for your selected items.",
        "url": "https://www.galleceylonspices.com/cart",
        "publisher": {
            "@type": "Organization",
            "name": "Galle Ceylon Spices International (Pvt) Ltd",
            "logo": {
                "@type": "ImageObject",
                "url": "%PUBLIC_URL%/logo.png"
            },
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "448/2, Nawadagala",
                "addressLocality": "Elpitiya",
                "postalCode": "80000",
                "addressCountry": "Sri Lanka"
            },
            "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+94-77-929-1829",
                "contactType": "Customer Service",
                "contactOption": "WhatsApp",
                "areaServed": "Worldwide",
                "availableLanguage": "English"
            }
        },
        "mainEntity": {
            "@type": "OfferCatalog",
            "name": "Shopping Cart",
            "itemListElement": cartItems.map((item, index) => ({
                "@type": "Offer",
                "itemOffered": {
                    "@type": "Product",
                    "name": `${item.category} - ${item.type}`,
                    "description": `Premium ${item.category} (${item.type}) with pack weight of ${item.packWeight} ${item.unit}`,
                    "sku": item._id
                },
                "quantity": item.quantity
            }))
        }
    };

    return (
        <div className="cart-page-container my-4 py-4">
            <SEO
                title="Your Cart - Galle Ceylon Spices International"
                description="View and manage your shopping cart with premium Ceylon cinnamon and spices from Galle Ceylon Spices International. Request a quote via WhatsApp for your selected items."
                keywords="cart, Ceylon cinnamon cart, Galle Ceylon Spices cart, spice shopping cart, WhatsApp quote, Sri Lankan spices"
                canonicalUrl="https://www.galleceylonspices.com/cart"
                ogImage="%PUBLIC_URL%/logo.png"
                structuredData={structuredData}
            />
            <Container>
                <Row className="mb-3">
                    <Col>
                        <h1
                            className="display-5 text-center"
                            style={{ fontFamily: 'Copperplate', color: '#C19A6B', fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}
                        >
                            <FaShoppingCart className="me-2" aria-hidden="true" /> Your Cart
                        </h1>
                    </Col>
                </Row>

                {!emailSubmitted ? (
                    <Row className="mb-3">
                        <Col>
                            <Form>
                                <Form.Group className="mb-3" controlId="formEmail">
                                    <Form.Label
                                        style={{
                                            fontFamily: 'Georgia',
                                            color: '#355E3B',
                                            fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                        }}
                                    >
                                        Enter Your Email to View Cart
                                    </Form.Label>
                                    <Form.Control
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter your email address"
                                        style={{
                                            fontFamily: 'Georgia',
                                            borderColor: '#C19A6B',
                                            color: '#355E3B',
                                            fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                            padding: 'clamp(6px, 1.5vw, 8px)',
                                        }}
                                        aria-label="Email address for cart access"
                                        required
                                    />
                                </Form.Group>
                                <Button
                                    variant="primary"
                                    onClick={handleEmailSubmit}
                                    style={{
                                        backgroundColor: '#C19A6B',
                                        borderColor: '#C19A6B',
                                        padding: 'clamp(8px, 2vw, 10px) clamp(16px, 3vw, 20px)',
                                        fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                                        fontFamily: 'Georgia',
                                    }}
                                    aria-label="Submit email to view cart"
                                >
                                    Submit Email
                                </Button>
                            </Form>
                        </Col>
                    </Row>
                ) : cartItems.length === 0 ? (
                    <Row>
                        <Col className="text-center">
                            <p className="lead" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)' }}>
                                Your cart is currently empty.
                            </p>
                            <Button
                                href="/shop"
                                variant="outline-dark"
                                className="mt-2"
                                style={{
                                    borderColor: '#C19A6B',
                                    color: '#C19A6B',
                                    fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                    padding: '8px 16px',
                                }}
                                aria-label="Continue shopping"
                            >
                                Continue Shopping
                            </Button>
                        </Col>
                    </Row>
                ) : (
                    <>
                        <Row>
                            <Col>
                                <Table striped bordered hover responsive className="shadow-sm" aria-label="Shopping cart items">
                                    <thead>
                                        <tr>
                                            <th style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Category</th>
                                            <th style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Type</th>
                                            <th style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Quantity</th>
                                            <th style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Pack Weight</th>
                                            <th style={{ fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cartItems.map((item) => (
                                            <tr key={item._id}>
                                                <td style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                                                    {item.category}
                                                </td>
                                                <td style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>{item.type}</td>
                                                <td style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                                                    {item.quantity}
                                                </td>
                                                <td style={{ fontSize: 'clamp(0.85rem, 1.8vw, 0.95rem)' }}>
                                                    {item.packWeight} {item.unit}
                                                </td>
                                                <td>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveItem(item._id)}
                                                        style={{ padding: 'clamp(4px, 1vw, 6px)' }}
                                                        aria-label={`Remove ${item.category} ${item.type} from cart`}
                                                    >
                                                        <FaTrash size={14} aria-hidden="true" />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col>
                                <Form>
                                    <Form.Group className="mb-3" controlId="formCustomerName">
                                        <Form.Label
                                            style={{
                                                fontFamily: 'Georgia',
                                                color: '#355E3B',
                                                fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                            }}
                                        >
                                            Your Name
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={customerName}
                                            onChange={(e) => setCustomerName(e.target.value)}
                                            placeholder="Enter your full name"
                                            style={{
                                                fontFamily: 'Georgia',
                                                borderColor: '#C19A6B',
                                                color: '#355E3B',
                                                fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                                padding: 'clamp(6px, 1.5vw, 8px)',
                                            }}
                                            aria-label="Full name for quote request"
                                            required
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3" controlId="formContactNumber">
                                        <Form.Label
                                            style={{
                                                fontFamily: 'Georgia',
                                                color: '#355E3B',
                                                fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                            }}
                                        >
                                            Contact Number
                                        </Form.Label>
                                        <Form.Control
                                            type="tel"
                                            value={contactNumber}
                                            onChange={(e) => setContactNumber(e.target.value)}
                                            placeholder="Enter your contact number (e.g., +94779291829)"
                                            style={{
                                                fontFamily: 'Georgia',
                                                borderColor: '#C19A6B',
                                                color: '#355E3B',
                                                fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                                                padding: 'clamp(6px, 1.5vw, 8px)',
                                            }}
                                            aria-label="Contact number for quote request"
                                            required
                                        />
                                    </Form.Group>
                                </Form>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col className="text-center">
                                <Button
                                    variant="primary"
                                    style={{
                                        backgroundColor: '#C19A6B',
                                        borderColor: '#C19A6B',
                                        padding: 'clamp(8px, 2vw, 10px) clamp(16px, 3vw, 20px)',
                                        fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
                                        fontFamily: 'Georgia',
                                    }}
                                    onClick={handleSendQuote}
                                    aria-label="Send cart quote via WhatsApp"
                                >
                                    Send Quote via WhatsApp
                                </Button>
                            </Col>
                        </Row>
                    </>
                )}
            </Container>

            <style>
                {`
                    @media (max-width: 576px) {
                        .cart-page-container {
                            padding-left: 0.5rem !important;
                            padding-right: 0.5rem !important;
                        }
                        .cart-page-container.my-4 {
                            margin-top: 1rem !important;
                            margin-bottom: 1rem !important;
                        }
                        .cart-page-container.py-4 {
                            padding-top: 1rem !important;
                            padding-bottom: 1rem !important;
                        }
                        .cart-page-container .mb-3 {
                            margin-bottom: 0.5rem !important;
                        }
                        .cart-page-container .mt-3 {
                            margin-top: 0.5rem !important;
                        }
                        .cart-page-container .table {
                            font-size: 0.85rem !important;
                        }
                        .cart-page-container .table td, .cart-page-container .table th {
                            padding: 0.5rem !important;
                        }
                        .cart-page-container .btn-danger {
                            padding: 3px !important;
                        }
                        .cart-page-container .btn-danger svg {
                            width: 12px !important;
                            height: 12px !important;
                        }
                        .cart-page-container .form-control {
                            padding: 4px !important;
                            font-size: 0.8rem !important;
                        }
                        .cart-page-container .btn {
                            padding: 6px 12px !important;
                            font-size: 0.9rem !important;
                        }
                    }
                    @media (max-width: 576px) and (orientation: landscape) {
                        .cart-page-container .table {
                            font-size: 0.8rem !important;
                        }
                        .cart-page-container .table td, .cart-page-container .table th {
                            padding: 0.3rem !important;
                        }
                        .cart-page-container .btn-danger {
                            padding: 2px !important;
                        }
                        .cart-page-container .btn-danger svg {
                            width: 10px !important;
                            height: 10px !important;
                        }
                        .cart-page-container .form-control {
                            padding: 4px !important;
                            font-size: 0.8rem !important;
                        }
                        .cart-page-container .btn {
                            padding: 6px 12px !important;
                            font-size: 0.9rem !important;
                        }
                    }
                    @media (min-width: 577px) and (max-width: 768px) {
                        .cart-page-container {
                            padding-left: 1rem !important;
                            padding-right: 1rem !important;
                        }
                        .cart-page-container.my-4 {
                            margin-top: 2rem !important;
                            margin-bottom: 2rem !important;
                        }
                        .cart-page-container.py-4 {
                            padding-top: 2rem !important;
                            padding-bottom: 2rem !important;
                        }
                        .cart-page-container .mb-3 {
                            margin-bottom: 1rem !important;
                        }
                        .cart-page-container .mt-3 {
                            margin-top: 1rem !important;
                        }
                        .cart-page-container .table {
                            font-size: 0.9rem !important;
                        }
                        .cart-page-container .table td, .cart-page-container .table th {
                            padding: 0.75rem !important;
                        }
                    }
                    @media (min-width: 769px) and (max-width: 1024px) {
                        .cart-page-container {
                            padding-left: 1.5rem !important;
                            padding-right: 1.5rem !important;
                        }
                        .cart-page-container.my-4 {
                            margin-top: 3rem !important;
                            margin-bottom: 3rem !important;
                        }
                        .cart-page-container.py-4 {
                            padding-top: 3rem !important;
                            padding-bottom: 3rem !important;
                        }
                        .cart-page-container .mb-3 {
                            margin-bottom: 1.5rem !important;
                        }
                        .cart-page-container .mt-3 {
                            margin-top: 1.5rem !important;
                        }
                        .cart-page-container .table {
                            font-size: 0.95rem !important;
                        }
                        .cart-page-container .table td, .cart-page-container .table th {
                            padding: 1rem !important;
                        }
                    }
                    @media (min-width: 1025px) {
                        .cart-page-container {
                            padding-left: 2rem !important;
                            padding-right: 2rem !important;
                        }
                        .cart-page-container.my-4 {
                            margin-top: 4rem !important;
                            margin-bottom: 4rem !important;
                        }
                        .cart-page-container.py-4 {
                            padding-top: 4rem !important;
                            padding-bottom: 4rem !important;
                        }
                        .cart-page-container .mb-3 {
                            margin-bottom: 2rem !important;
                        }
                        .cart-page-container .mt-3 {
                            margin-top: 2rem !important;
                        }
                        .cart-page-container .table {
                            font-size: 1rem !important;
                        }
                        .cart-page-container .table td, .cart-page-container .table th {
                            padding: 1rem !important;
                        }
                    }
                `}
            </style>
        </div>
    );
}

export default Cart;